import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Card,
  CardContent,
  Fade,
  Zoom,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { ReactComponent as BalanceIcon } from '../../assets/ofa/Balance_Icon.svg'
import { ReactComponent as CoinsIcon } from '../../assets/ofa/Coins_Icon.svg'
import { ReactComponent as PeopleIcon } from '../../assets/ofa/People_Icon.svg'
import { DiversityDimenstionConfig, Module, ModuleConfig } from '../../shared/model'
import { FsOFAOverall } from '../../shared/model/models'
import { getFormattedString } from '../../shared/utils'

const FsOFAInfoCards = ({
  diversityDimenstionConfig,
  moduleConfig,
  showInfoCards,
  shouldRecalculate,
  saveAction,
  values,
}: {
  diversityDimenstionConfig: DiversityDimenstionConfig | undefined
  moduleConfig: ModuleConfig | undefined
  showInfoCards: boolean
  shouldRecalculate: boolean
  saveAction: () => void
  values: FsOFAOverall | null
}) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()

  const isCareer = moduleConfig?.module === Module.CAREER

  return (
    values && (
      <Box sx={{ position: 'relative' }}>
        <Stack
          direction="row"
          mb={2}
          justifyContent="center"
          sx={{ filter: shouldRecalculate ? 'blur(12px)' : '' }}
        >
          <Zoom in={showInfoCards} style={{ transitionDelay: showInfoCards ? '100ms' : '0ms' }}>
            <Card
              sx={{
                width: 400,
                boxShadow: `2px 2px 8px 2px ${moduleConfig?.fsColor.primary}66`,
              }}
            >
              <CardContent sx={{ pb: '16px !important' }}>
                <Stack direction="row" alignItems="center" mb={2}>
                  <BalanceIcon color={diversityDimenstionConfig?.fsColor.primary} />
                  <Typography className="font-semibold" mx={1} variant="body2">
                    {`${t('unadjusteder')} ${t(`${moduleConfig?.module}_gap` as any)} ${t(
                      'ofa.after.actions',
                    )}`}
                  </Typography>
                </Stack>
                <Typography variant="h2" textAlign="center">{`${getFormattedString(
                  i18n.language,
                  `${values.overallGap}`,
                  1,
                  1,
                )} %`}</Typography>
                <Typography
                  variant="body2"
                  fontWeight="semibold"
                  textAlign="center"
                  display="block"
                  mt={1}
                >{`${t('ofa.current')}: ${getFormattedString(
                  i18n.language,
                  `${values.currentOverallGap}`,
                  1,
                  1,
                )} %`}</Typography>
              </CardContent>
            </Card>
          </Zoom>
          <Zoom in={showInfoCards} style={{ transitionDelay: showInfoCards ? '150ms' : '0ms' }}>
            <Card
              sx={{
                width: 400,
                ml: 2,
                boxShadow: `2px 2px 8px 2px ${moduleConfig?.fsColor.primary}66`,
              }}
            >
              <CardContent sx={{ pb: '16px !important' }}>
                <Stack direction="row" alignItems="center" mb={2}>
                  <CoinsIcon color={diversityDimenstionConfig?.fsColor.primary} />
                  <Typography className="font-semibold" ml={1} variant="body2">
                    {t('ofa.additional.wage')}
                  </Typography>
                </Stack>
                <Typography variant="h2" textAlign="center">{`${getFormattedString(
                  i18n.language,
                  `${values.additionalCost}`,
                  2,
                  2,
                  true,
                )} \u20ac/${t('ofa.month')}`}</Typography>
                <Typography
                  variant="caption"
                  textAlign="center"
                  display="block"
                  fontWeight="semibold"
                  mt={1}
                >{`${getFormattedString(i18n.language, `${values.percentOfPayroll}`, 0, 2)} % ${t(
                  'ofa.current.monthly',
                )}`}</Typography>
              </CardContent>
            </Card>
          </Zoom>
          {!isCareer && (
            <Zoom in={showInfoCards} style={{ transitionDelay: showInfoCards ? '200ms' : '0ms' }}>
              <Card
                sx={{
                  width: 400,
                  ml: 2,
                  boxShadow: `2px 2px 8px 2px ${moduleConfig?.fsColor.primary}66`,
                }}
              >
                <CardContent sx={{ pb: '16px !important' }}>
                  <Stack direction="row" alignItems="center" mb={2}>
                    <PeopleIcon color={diversityDimenstionConfig?.fsColor.primary} />
                    <Typography className="font-semibold" ml={1} variant="body2">
                      {t('ofa.no.of.employees')}
                    </Typography>
                  </Stack>
                  <Typography variant="h2" textAlign="center">
                    {getFormattedString(i18n.language, `${values.employeesConsidered}`, 0, 0)}{' '}
                    {t('ofa.employees')}
                  </Typography>
                  <Typography
                    variant="caption"
                    textAlign="center"
                    display="block"
                    fontWeight="semibold"
                    mt={1}
                  >{`${getFormattedString(
                    i18n.language,
                    `${values.percentEmployeesConsidered}`,
                    0,
                    2,
                  )} % ${t('ofa.all.employees')}`}</Typography>
                </CardContent>
              </Card>
            </Zoom>
          )}
        </Stack>
        {shouldRecalculate && (
          <Stack
            sx={{
              position: 'absolute',
              top: 0,
              backgroundColor: theme.palette.action.hoverOpacity,
              borderRadius: 2,
              boxShadow: `0px 1px 5px 0px ${theme.palette.text.secondary}`,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 16,
            }}
          >
            <Typography mb={2} fontWeight="semibold" textAlign="center">
              {t('ofa.save.desc')}
            </Typography>
            <Button variant="contained" sx={{ width: 'fit-content' }} onClick={saveAction}>
              {t('ofa.save')}
            </Button>
          </Stack>
        )}
      </Box>
    )
  )
}

export default FsOFAInfoCards
